import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Autocomplete,
  Box,
  CircularProgress,
  Divider,
  Switch,
  TextField,
} from "@mui/material";
import { CloseRounded, EventRepeatOutlined, PlaceOutlined } from "@mui/icons-material";
import { useBreakpoint } from "../../../../shared/hooks/useBreakpoint";
import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import {
  createWorkOrder,
  updateWorkOrder,
  fetchWorkOrder,
  previewRecurrence,
  regenerateFutureInstances,
  fetchAssignableUsers,
} from "../../store/workOrdersSlice";
import {
  WorkOrderTypeCode,
  WorkOrderPriorityCode,
  WorkOrder,
  WorkOrderDetail,
  CreateWorkOrder as CreateWOModel,
  UpdateWorkOrder as UpdateWOModel,
  Location,
} from "../../models/models";
import { getMachines } from "../../../machinery/customerMachinerySlice";
import { getUsers } from "../../../settings/customerUsersSlice";
import { Machine, CustomerMachineryState } from "../../../machinery/models/models";
import { User, UserState } from "../../../settings/models/userModels";
import useTranslation from "../../../../shared/hooks/useTranslation";
import accountContext from "../../../../account/accountContext";
import { Roles } from "../../../../account/models/AccountModels";
import { RootState } from "../../../../core/store";
import api from "../../../../core/api";
import RecurrenceRulePicker from "./RecurrenceRulePicker";
import TeamPicker from "../teams/TeamPicker";
import ScheduleTimePicker from "./ScheduleTimePicker";
import {
  FormFieldLabel,
  FormTextField,
  FormTextarea,
  FormDatePicker,
  FormChipSelector,
  FormFileUpload,
} from "../../../../shared/components/redesign/forms";
import type { ChipOption, UploadedFile } from "../../../../shared/components/redesign/forms";
import workOrdersApi from "../../api/workOrdersApi";

// ─── Helper: convert base64 data URI back to File ────────────
const dataUriToFile = (dataUri: string, name: string): File => {
  const [header, base64] = dataUri.split(",");
  const mime = header.match(/:(.*?);/)?.[1] || "application/octet-stream";
  const bytes = atob(base64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  return new File([arr], name, { type: mime });
};

// ─── Panel-specific styling constants (from design spec) ─────
const PANEL_WIDTH = 580;
const PANEL_BG = "#FFFFFF";
const BORDER_COLOR = "#E8E5DF";
const SECTION_LABEL_COLOR = "#8C9099";
const INPUT_BG = "#F7F5F0";
const INPUT_BORDER = "#E8E5DF";
const FOCUS_COLOR = "#D4572B"; // terracotta accent
const TEXT_PRIMARY = "#0C1117";
const TEXT_BODY = "#4A4F57";
const FONT_SORA = "'Sora', sans-serif";
const FONT_MONO = "'JetBrains Mono', monospace";

// ─── Date helpers ────────────────────────────────────────────
const parseDate = (s: string): Date | null => (s ? new Date(s) : null);
const formatDate = (d: Date | null): string =>
  d ? d.toISOString().substring(0, 10) : "";

// ─── Chip configs (panel-specific styling) ───────────────────
const buildTypeChips = (translate: (k: string) => string): ChipOption<number>[] => [
  { code: WorkOrderTypeCode.Reactive, color: FOCUS_COLOR, label: translate("typesShort.reactive") },
  { code: WorkOrderTypeCode.PreventiveMaintenance, color: FOCUS_COLOR, label: translate("typesShort.preventiveMaintenance") },
  { code: WorkOrderTypeCode.PredictiveMaintenance, color: FOCUS_COLOR, label: translate("typesShort.predictiveMaintenance") },
  { code: WorkOrderTypeCode.Inspection, color: FOCUS_COLOR, label: translate("typesShort.inspection") },
  { code: WorkOrderTypeCode.GeneralTask, color: FOCUS_COLOR, label: translate("typesShort.generalTask") },
];

const buildPriorityChips = (translate: (k: string) => string): ChipOption<number>[] => [
  { code: WorkOrderPriorityCode.Critical, color: "#D4572B", label: translate("priorities.critical"), dotOnly: true },
  { code: WorkOrderPriorityCode.High, color: "#E8A817", label: translate("priorities.high"), dotOnly: true },
  { code: WorkOrderPriorityCode.Normal, color: "#B8B5AF", label: translate("priorities.normal"), dotOnly: true },
  { code: WorkOrderPriorityCode.Low, color: "#3B82F6", label: translate("priorities.low"), dotOnly: true },
];

// ─── Panel autocomplete sx (terracotta focus) ────────────────
const panelTextFieldSx = {
  "& .MuiOutlinedInput-root": {
    fontFamily: FONT_SORA,
    fontSize: "14px",
    borderRadius: "8px",
    backgroundColor: INPUT_BG,
    "& fieldset": { borderColor: INPUT_BORDER, borderWidth: "1.5px" },
    "&:hover fieldset": { borderColor: FOCUS_COLOR },
    "&.Mui-focused fieldset": { borderColor: FOCUS_COLOR, borderWidth: "1.5px" },
  },
  "& .MuiSvgIcon-root": { color: "#4A4F57" },
};

// ─── Avatar color palette for assignees ─────────────────────
const AVATAR_COLORS = ["#D4572B", "#3B82F6", "#2D8C4E", "#8C6BD4", "#E8A817"];
const getAvatarColor = (index: number) => AVATAR_COLORS[index % AVATAR_COLORS.length];

// ─── Terracotta DatePicker popper overrides ─────────────────
const datePickerTerracottaPopperSx = {
  "& .MuiPickersDay-root.Mui-selected": {
    backgroundColor: "#D4572B",
    "&:hover": { backgroundColor: "#B84A24" },
  },
  "& .MuiPickersDay-root:hover": {
    backgroundColor: "rgba(212,87,43,0.08)",
  },
  "& .MuiPickersDay-root.Mui-selected:hover": {
    backgroundColor: "#B84A24",
  },
  "& .MuiPickersDay-root:focus.Mui-selected": {
    backgroundColor: "#D4572B",
  },
  "& .MuiPickersYear-yearButton.Mui-selected": {
    backgroundColor: "#D4572B",
    "&:hover": { backgroundColor: "#B84A24" },
  },
  "& .MuiPickersMonth-monthButton.Mui-selected": {
    backgroundColor: "#D4572B",
    "&:hover": { backgroundColor: "#B84A24" },
  },
};

// ─── Shared slotProps for Autocomplete dropdown popups ────────
const autocompletePaperSlotProps = {
  paper: {
    sx: {
      backgroundColor: "#FFFFFF",
      borderRadius: "12px",
      border: `1px solid ${BORDER_COLOR}`,
      boxShadow: "0 8px 32px rgba(12,17,23,0.08)",
      padding: "6px",
      mt: "4px",
      "& .MuiAutocomplete-listbox": {
        padding: "0",
        "& .MuiAutocomplete-option": {
          padding: "0 !important",
          borderRadius: "8px",
          margin: "1px 0",
          minHeight: "unset",
          "&:hover, &.Mui-focused": { backgroundColor: "#F7F5F0" },
          "&[aria-selected='true']": {
            backgroundColor: "rgba(212,87,43,0.06) !important",
            "& .option-primary-text": { color: FOCUS_COLOR },
          },
        },
      },
      "& .MuiAutocomplete-input": {
        fontFamily: FONT_SORA,
        fontSize: "12px",
      },
    },
  },
};

// ─── Props ───────────────────────────────────────────────────
interface CreateWorkOrderPanelProps {
  open: boolean;
  onClose: () => void;
  onCreated: (wo: WorkOrder) => void;
  prefill?: {
    serviceReportId?: string;
    serviceReportNumber?: string;
    machineId?: string;
    title?: string;
    description?: string;
    workInstructions?: string;
    parentWorkOrderId?: string;
    locationId?: string;
    priorityCode?: number;
    typeId?: number;
  };
  /** When set, the panel opens in EDIT mode for this WO id */
  editWorkOrderId?: string;
  /** When true, update + regenerate future instances (recurring templates) */
  editAllFuture?: boolean;
}

const CreateWorkOrderPanel: React.FC<CreateWorkOrderPanelProps> = ({
  open,
  onClose,
  onCreated,
  prefill,
  editWorkOrderId,
  editAllFuture,
}) => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("operations");
  const canManage = accountContext.user?.role !== Roles.WORKER;
  const { isPhone } = useBreakpoint();

  // ─── Lock body scroll when panel is open ─────────────────
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  const { loading, recurrencePreview } = useAppSelector((state: RootState) => state.workOrders);

  const { machines, loading: machinesLoading } = useAppSelector<CustomerMachineryState>(
    (state) => state.customerMachinery
  );
  const allMachines = useMemo(() => Object.values(machines).flat(), [machines]);

  const { users, loading: usersLoading } = useAppSelector<UserState>(
    (state) => state.customerUsers
  );

  // RC-5: Location-filtered assignable users
  const { assignableUsers, assignableUsersLoading } = useAppSelector(
    (state: RootState) => state.workOrders
  );

  // ─── Form state ──────────────────────────────────────────
  const [title, setTitle] = useState("");
  const [workInstructions, setWorkInstructions] = useState("");
  const [description, setDescription] = useState("");
  const [typeId, setTypeId] = useState<number>(WorkOrderTypeCode.Reactive);
  const [priority, setPriority] = useState<number>(WorkOrderPriorityCode.Normal);
  const [machineId, setMachineId] = useState("");
  const [selectedMachine, setSelectedMachine] = useState<Machine | null>(null);
  const [locationId, setLocationId] = useState("");
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [allLocations, setAllLocations] = useState<Location[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [assignedToUserId, setAssignedToUserId] = useState("");
  const [assignedToUserName, setAssignedToUserName] = useState("");
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [dueDate, setDueDate] = useState("");
  const [scheduledStartDate, setScheduledStartDate] = useState("");
  const [scheduledStartTime, setScheduledStartTime] = useState("");
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [teamId, setTeamId] = useState<string | undefined>(undefined);
  const [autoAssignToLeastLoaded, setAutoAssignToLeastLoaded] = useState(true);
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [scheduledEndDate, setScheduledEndDate] = useState("");
  const [estimatedDuration, setEstimatedDuration] = useState("");
  const [completionNotes, setCompletionNotes] = useState("");
  const [costStr, setCostStr] = useState("");
  const [serviceReportId, setServiceReportId] = useState("");
  const [parentWorkOrderId, setParentWorkOrderId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrenceRule, setRecurrenceRule] = useState("");
  const [editDataLoaded, setEditDataLoaded] = useState(false);

  const isEditMode = Boolean(editWorkOrderId);

  // ─── Sorted locations: parents first, then their children ─
  const sortedLocations = useMemo(() => {
    const parents = allLocations.filter((l) => !l.parentLocationId);
    const result: Location[] = [];
    parents.forEach((parent) => {
      result.push(parent);
      allLocations
        .filter((l) => l.parentLocationId === parent.id)
        .forEach((child) => result.push(child));
    });
    return result;
  }, [allLocations]);

  // ─── Memoised chip options ───────────────────────────────
  const typeChips = useMemo(() => buildTypeChips(translate), [translate]);
  const priorityChips = useMemo(() => buildPriorityChips(translate), [translate]);

  // Combined start date: full ISO datetime when time is set, date-only otherwise
  const combinedStartDate = useMemo(() => {
    if (!scheduledStartDate) return undefined;
    if (scheduledStartTime) return `${scheduledStartDate}T${scheduledStartTime}:00.000Z`;
    return scheduledStartDate;
  }, [scheduledStartDate, scheduledStartTime]);

  // Recurrence is available for Admin users on all WO types (create mode only)
  const showRecurrenceOption = canManage && !isEditMode;

  // Preview recurrence when rule changes
  useEffect(() => {
    if (isRecurring && recurrenceRule) {
      const today = new Date().toISOString().substring(0, 10);
      dispatch(previewRecurrence({ recurrenceRule, startDate: today, count: 5 }));
    }
  }, [isRecurring, recurrenceRule, dispatch]);

  // (Removed: useEffect that reset recurrence on type change — all types are now eligible)

  // ─── Load reference data when panel opens ────────────────
  useEffect(() => {
    if (!open) return;
    dispatch(getMachines({}));
    dispatch(getUsers());
  }, [dispatch, open]);

  // RC-5: Fetch assignable users when locationId changes
  useEffect(() => {
    if (!open) return;
    dispatch(fetchAssignableUsers(locationId || undefined));
  }, [dispatch, open, locationId]);

  // ─── Load existing WO in edit mode ───────────────────────
  useEffect(() => {
    if (!open || !editWorkOrderId) return;
    setEditDataLoaded(false);
    dispatch(fetchWorkOrder(editWorkOrderId));
  }, [open, editWorkOrderId, dispatch]);

  // ─── Pre-fill form from loaded WO (edit mode) ───────────
  const { currentWorkOrder: editWO } = useAppSelector((state: RootState) => state.workOrders);
  useEffect(() => {
    if (!open || !isEditMode || !editWO || editDataLoaded) return;
    if (editWO.id !== editWorkOrderId) return;

    setTitle(editWO.title || "");
    setDescription(editWO.description || "");
    setWorkInstructions(editWO.workInstructions || "");
    setTypeId(editWO.workOrderTypeId);
    setPriority(editWO.priorityCode);
    setMachineId(editWO.machineId || "");
    setLocationId(editWO.locationId || "");
    setAssignedToUserId(editWO.assignedToUserId || "");
    setAssignedToUserName(editWO.assignedToUserName || "");
    setDueDate(editWO.dueDate ? editWO.dueDate.substring(0, 10) : "");
    setScheduledEndDate(editWO.scheduledEndDate ? editWO.scheduledEndDate.substring(0, 10) : "");
    setEstimatedDuration(editWO.estimatedDurationMinutes?.toString() || "");
    setCompletionNotes(editWO.completionNotes || "");
    setCostStr(editWO.cost?.toString() || "");
    setServiceReportId(editWO.serviceReportId || "");
    setParentWorkOrderId(editWO.parentWorkOrderId || "");
    setTeamId(editWO.teamId || undefined);

    // Handle scheduled start date + time
    if (editWO.scheduledStartDate) {
      setScheduledStartDate(editWO.scheduledStartDate.substring(0, 10));
      const d = new Date(editWO.scheduledStartDate);
      const hours = d.getUTCHours();
      const minutes = d.getUTCMinutes();
      if (hours !== 0 || minutes !== 0) {
        setScheduledStartTime(`${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`);
        setShowTimePicker(true);
      }
    }

    // Resolve machine object
    if (editWO.machineId && allMachines.length > 0) {
      const found = allMachines.find((m) => m.id === editWO.machineId);
      if (found) setSelectedMachine(found);
    }
    // Resolve user object
    if (editWO.assignedToUserId && users.length > 0) {
      const found = users.find((u) => u.id === editWO.assignedToUserId);
      if (found) setSelectedUser(found);
    }
    // Resolve location object (deferred until allLocations loads — handled below)

    setEditDataLoaded(true);
  }, [open, isEditMode, editWO, editWorkOrderId, editDataLoaded, allMachines, users]);

  // ─── Resolve location after locations load (edit mode) ────
  useEffect(() => {
    if (!isEditMode || !editWO || !editDataLoaded) return;
    if (editWO.locationId && allLocations.length > 0 && !selectedLocation) {
      const loc = allLocations.find((l) => l.id === editWO.locationId);
      if (loc) {
        setSelectedLocation(loc);
        setLocationId(loc.id);
      }
    }
  }, [isEditMode, editWO, editDataLoaded, allLocations, selectedLocation]);

  // ─── Load locations ──────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const fetchLocations = async () => {
      try {
        setLocationsLoading(true);
        const config = { baseURL: (window as any).app.config.customersApiBaseUrl };
        const res = await api.get<any[]>("/v1/locations", config);
        const raw = res.data || [];
        const flat: Location[] = [];
        raw.forEach((parent: any) => {
          flat.push({
            id: parent.id,
            name: parent.name,
            parentLocationId: parent.parentLocationId,
            parentLocationName: parent.parentLocationName,
          });
          (parent.childLocations || []).forEach((child: any) => {
            flat.push({
              id: child.id,
              name: child.name,
              parentLocationId: child.parentLocationId || parent.id,
              parentLocationName: parent.name,
            });
          });
        });
        setAllLocations(flat);
      } catch {
        setAllLocations([]);
      } finally {
        setLocationsLoading(false);
      }
    };
    fetchLocations();
  }, [open]);

  // ─── Apply prefill when panel opens ──────────────────────
  useEffect(() => {
    if (open && prefill) {
      if (prefill.title) setTitle(prefill.title);
      if (prefill.description) setDescription(prefill.description);
      if (prefill.machineId) {
        setMachineId(prefill.machineId);
        const found = allMachines.find((m) => m.id === prefill.machineId);
        if (found) setSelectedMachine(found);
      }
      if (prefill.locationId && allLocations.length > 0) {
        const loc = allLocations.find((l) => l.id === prefill.locationId);
        if (loc) {
          setSelectedLocation(loc);
          setLocationId(loc.id);
        }
      }
      if (prefill.workInstructions) setWorkInstructions(prefill.workInstructions);
      if (prefill.priorityCode != null) setPriority(prefill.priorityCode);
      if (prefill.typeId != null) setTypeId(prefill.typeId);
    }
  }, [open, prefill, allMachines, allLocations]);

  // ─── Reset form when panel closes ────────────────────────
  useEffect(() => {
    if (!open) {
      setTitle("");
      setWorkInstructions("");
      setDescription("");
      setTypeId(WorkOrderTypeCode.Reactive);
      setPriority(WorkOrderPriorityCode.Normal);
      setMachineId("");
      setSelectedMachine(null);
      setLocationId("");
      setSelectedLocation(null);
      setAssignedToUserId("");
      setAssignedToUserName("");
      setSelectedUser(null);
      setDueDate("");
      setScheduledStartDate("");
      setScheduledStartTime("");
      setScheduledEndDate("");
      setEstimatedDuration("");
      setCompletionNotes("");
      setCostStr("");
      setServiceReportId("");
      setParentWorkOrderId("");
      setShowTimePicker(false);
      setTeamId(undefined);
      setAutoAssignToLeastLoaded(true);
      setFiles([]);
      setSubmitting(false);
      setIsRecurring(false);
      setRecurrenceRule("");
      setEditDataLoaded(false);
    }
  }, [open]);

  // ─── Esc key handler ─────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [open, onClose]);

  // ─── Submit (handles both Create and Edit) ───────────────
  const handleSubmit = useCallback(async () => {
    if (!title.trim() || submitting) return;
    setSubmitting(true);

    const base: CreateWOModel = {
      title: title.trim(),
      description: description.trim() || undefined,
      workInstructions: workInstructions.trim() || undefined,
      workOrderTypeId: typeId,
      priorityCode: priority,
      machineId: machineId || undefined,
      locationId: locationId || undefined,
      assignedToUserId: assignedToUserId || undefined,
      assignedToUserName: assignedToUserName || undefined,
      dueDate: dueDate || undefined,
      scheduledStartDate: combinedStartDate || undefined,
      scheduledEndDate: scheduledEndDate || undefined,
      estimatedDurationMinutes: estimatedDuration ? parseInt(estimatedDuration, 10) : undefined,
      teamId: teamId || undefined,
      autoAssignToLeastLoaded: teamId ? autoAssignToLeastLoaded : undefined,
      recurrenceRule: isRecurring && recurrenceRule ? recurrenceRule : undefined,
      serviceReportId: isEditMode ? (serviceReportId || undefined) : (prefill?.serviceReportId || undefined),
      serviceReportNumber: isEditMode ? undefined : (prefill?.serviceReportNumber || undefined),
      parentWorkOrderId: isEditMode ? (parentWorkOrderId || undefined) : (prefill?.parentWorkOrderId || undefined),
    };

    try {
      if (isEditMode && editWorkOrderId) {
        // ─── Edit mode ─────────────────────────────────────
        const updateModel: UpdateWOModel = {
          ...base,
          completionNotes: completionNotes.trim() || undefined,
          cost: costStr ? parseFloat(costStr) : undefined,
        };
        const updated = await dispatch(updateWorkOrder({ id: editWorkOrderId, model: updateModel })).unwrap();

        // Upload new files if any
        if (files.length > 0) {
          for (const f of files) {
            try {
              const rawFile = dataUriToFile(f.content, f.name);
              await workOrdersApi.uploadFile(editWorkOrderId, rawFile);
            } catch {
              console.error("[WOPanel] file upload failed:", f.name);
            }
          }
        }

        // Regenerate future instances for recurring template edits
        if (editAllFuture) {
          try {
            await dispatch(regenerateFutureInstances(editWorkOrderId)).unwrap();
          } catch {
            console.error("[WOPanel] regenerateFutureInstances failed");
          }
        }

        // Refresh the detail page data
        dispatch(fetchWorkOrder(editWorkOrderId));
        onCreated(updated as unknown as WorkOrder);
      } else {
        // ─── Create mode ───────────────────────────────────
        const created = await dispatch(createWorkOrder(base)).unwrap();

        // Upload files if any (after WO is created)
        if (files.length > 0 && created.id) {
          for (const f of files) {
            try {
              const rawFile = dataUriToFile(f.content, f.name);
              await workOrdersApi.uploadFile(created.id, rawFile);
            } catch {
              console.error("[WOPanel] file upload failed:", f.name);
            }
          }
        }

        onCreated(created as unknown as WorkOrder);
      }
    } catch (err) {
      console.error("[WOPanel] submit failed:", err);
    } finally {
      setSubmitting(false);
    }
  }, [
    title, description, workInstructions, typeId, priority, machineId, locationId,
    assignedToUserId, assignedToUserName, dueDate, combinedStartDate,
    scheduledEndDate, estimatedDuration, completionNotes, costStr,
    serviceReportId, parentWorkOrderId,
    teamId, autoAssignToLeastLoaded, files, dispatch, onCreated, submitting,
    prefill, isRecurring, recurrenceRule, isEditMode, editWorkOrderId, editAllFuture,
  ]);

  const isValid = useMemo(() => title.trim().length > 0, [title]);

  // ─── Section Label component ─────────────────────────────
  const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Box
      sx={{
        fontFamily: FONT_MONO,
        fontSize: "11px",
        fontWeight: 600,
        color: SECTION_LABEL_COLOR,
        textTransform: "uppercase",
        letterSpacing: "0.1em",
        mb: "8px",
      }}
    >
      {children}
    </Box>
  );

  // ─── Section Divider ─────────────────────────────────────
  const SectionDivider = () => (
    <Box sx={{ height: "1px", backgroundColor: BORDER_COLOR, my: "16px" }} />
  );

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        right: 0,
        width: isPhone ? "100vw" : `${PANEL_WIDTH}px`,
        height: "100dvh",
        backgroundColor: PANEL_BG,
        borderLeft: isPhone ? "none" : `1px solid ${BORDER_COLOR}`,
        boxShadow: isPhone ? "none" : "-8px 0 32px rgba(0,0,0,0.06)",
        display: "flex",
        flexDirection: "column",
        zIndex: 1300,
        transform: open ? "translateX(0)" : isPhone ? "translateX(100vw)" : `translateX(${PANEL_WIDTH}px)`,
        transition: "transform 0.3s ease",
        pointerEvents: open ? "auto" : "none",
      }}
    >
      {/* ─── Fixed Header ─────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: isPhone ? "16px 16px 12px" : "20px 24px 16px",
          borderBottom: `1px solid ${BORDER_COLOR}`,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            fontFamily: FONT_SORA,
            fontSize: "18px",
            fontWeight: 700,
            color: TEXT_PRIMARY,
          }}
        >
          {isEditMode ? translate("editWorkOrder") : translate("createWorkOrder")}
        </Box>
        <Box
          onClick={onClose}
          sx={{
            width: "32px",
            height: "32px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#F2F0EB",
            borderRadius: "6px",
            cursor: "pointer",
            transition: "all 0.15s ease",
            "&:hover": { backgroundColor: "#E8E5DF" },
          }}
        >
          <CloseRounded sx={{ fontSize: 18, color: TEXT_BODY }} />
        </Box>
      </Box>

      {/* ─── Scrollable Body ──────────────────────────────── */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          padding: isPhone ? "16px" : "20px 24px",
          display: "flex",
          flexDirection: "column",
          gap: 0,
          scrollbarWidth: "thin",
          scrollbarColor: "#C1C7CD transparent",
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": { backgroundColor: "#C1C7CD", borderRadius: "3px" },
        }}
      >
        {/* "Edit All Future Instances" banner */}
        {isEditMode && editAllFuture && (
          <Box sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "10px 14px",
            mb: "12px",
            borderRadius: "10px",
            backgroundColor: "rgba(59, 130, 246, 0.05)",
            border: "1px solid rgba(59, 130, 246, 0.20)",
          }}>
            <EventRepeatOutlined sx={{ fontSize: 16, color: "#3B82F6" }} />
            <Box sx={{
              fontFamily: FONT_SORA,
              fontSize: "13px",
              fontWeight: 500,
              color: "#3B82F6",
            }}>
              {translate("recurringEdit.allFutureBanner") || "Changes will apply to all future instances"}
            </Box>
          </Box>
        )}

        {/* Title (required) */}
        <Box sx={{ mb: "12px" }}>
          <FormFieldLabel required>{translate("fields.title")}</FormFieldLabel>
          <FormTextField
            value={title}
            onChange={setTitle}
            placeholder={translate("fields.titlePlaceholder")}
          />
        </Box>

        {/* Work Instructions */}
        <Box sx={{ mb: "12px" }}>
          <FormFieldLabel>{translate("fields.workInstructions")}</FormFieldLabel>
          <FormTextarea
            value={workInstructions}
            onChange={setWorkInstructions}
            rows={3}
            minHeight="72px"
            placeholder={translate("fields.workInstructionsPlaceholder") || "What needs to be done? Steps, parts, tools..."}
          />
        </Box>

        {/* Type + Priority side by side (stacked on phone) */}
        <Box sx={{ display: "flex", flexDirection: isPhone ? "column" : "row", gap: isPhone ? "12px" : "24px", mb: "12px" }}>
          <Box sx={{ flex: isPhone ? "unset" : "0 0 58%" }}>
            <SectionLabel>{translate("fields.maintenanceType")}</SectionLabel>
            <FormChipSelector
              options={typeChips}
              value={typeId}
              onChange={setTypeId}
            />
          </Box>
          <Box sx={{
            flex: 1,
            /* Fix: dotOnly priority chip text should stay dark when selected */
            "& > div > div": {
              "& > div:last-child": { color: `${TEXT_PRIMARY} !important` },
            },
          }}>
            <SectionLabel>{translate("fields.priority")}</SectionLabel>
            <FormChipSelector
              options={priorityChips}
              value={priority}
              onChange={setPriority}
            />
          </Box>
        </Box>

        {/* Additional Context (renamed Description — moved below Type/Priority) */}
        <Box sx={{ mb: "12px" }}>
          <FormFieldLabel>{translate("fields.additionalContext")}</FormFieldLabel>
          <FormTextarea
            value={description}
            onChange={setDescription}
            rows={3}
            minHeight="72px"
            placeholder={translate("fields.additionalContextPlaceholder") || "Problem description or notes (auto-filled when linked to SR)"}
          />
        </Box>

        <SectionDivider />

        {/* ─── ASSIGNMENT Section ─────────────────────────── */}
        <SectionLabel>{translate("createPanel.assignment") || "Assignment"}</SectionLabel>

        {/* Machine + Location side by side (stacked on phone) */}
        <Box sx={{ display: "flex", flexDirection: isPhone ? "column" : "row", gap: 2, mb: "12px" }}>
          <Box sx={{ flex: 1 }}>
            <FormFieldLabel>{translate("fields.machine")}</FormFieldLabel>
            <Autocomplete
              size="small"
              options={allMachines}
              value={selectedMachine}
              loading={machinesLoading}
              getOptionLabel={(m) => m.model}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              onChange={(_, val) => {
                setSelectedMachine(val);
                setMachineId(val?.id || "");
                if (val?.locationId && !locationId) {
                  const loc = allLocations.find((l) => l.id === val.locationId);
                  if (loc) {
                    setSelectedLocation(loc);
                    setLocationId(loc.id);
                  }
                }
              }}
              slotProps={autocompletePaperSlotProps}
              renderOption={(props, option) => {
                const { key, ...rest } = props as any;
                return (
                  <li key={key} {...rest} style={{ padding: 0 }}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: "1px", width: "100%", padding: "9px 12px", borderRadius: "8px", minWidth: 0 }}>
                      <Box className="option-primary-text" sx={{ fontFamily: FONT_SORA, fontSize: "13px", fontWeight: 600, color: TEXT_PRIMARY, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {option.model}
                      </Box>
                      <Box sx={{ fontFamily: FONT_MONO, fontSize: "10px", fontWeight: 400, color: SECTION_LABEL_COLOR }}>
                        SN: {option.serialNumber || "—"}
                      </Box>
                    </Box>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={translate("fields.selectMachine")}
                  sx={panelTextFieldSx}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {machinesLoading ? <CircularProgress size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <FormFieldLabel>{translate("fields.location")}</FormFieldLabel>
            <Autocomplete
              size="small"
              options={sortedLocations}
              value={selectedLocation}
              loading={locationsLoading}
              getOptionLabel={(loc) => loc.name}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              onChange={(_, val) => {
                setSelectedLocation(val);
                setLocationId(val?.id || "");
              }}
              slotProps={autocompletePaperSlotProps}
              renderOption={(props, option) => {
                const { key, ...rest } = props as any;
                const isParent = !option.parentLocationId;
                // Show divider before parent items (except the first)
                const idx = sortedLocations.indexOf(option);
                const showDivider = isParent && idx > 0;
                return (
                  <React.Fragment key={key}>
                    {showDivider && (
                      <Divider sx={{ my: "2px", borderColor: "#F2F0EB" }} />
                    )}
                    <li {...rest} style={{ padding: 0 }}>
                      <Box sx={{
                        display: "flex", alignItems: "center", gap: "8px",
                        padding: isParent ? "9px 12px" : "9px 12px 9px 32px",
                        borderRadius: "8px", width: "100%",
                      }}>
                        {isParent && (
                          <PlaceOutlined sx={{ fontSize: 12, color: FOCUS_COLOR, flexShrink: 0 }} />
                        )}
                        <Box className={isParent ? "option-primary-text" : undefined} sx={{
                          fontFamily: FONT_SORA, fontSize: "13px",
                          fontWeight: isParent ? 600 : 500,
                          color: isParent ? TEXT_PRIMARY : TEXT_BODY,
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }}>
                          {option.name}
                        </Box>
                      </Box>
                    </li>
                  </React.Fragment>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={translate("fields.selectLocation")}
                  sx={panelTextFieldSx}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {locationsLoading ? <CircularProgress size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>
        </Box>

        {/* Assignee — RC-5: use location-filtered assignable users when location is set */}
        <Box sx={{ mb: "12px" }}>
          <FormFieldLabel>{translate("fields.assignees")}</FormFieldLabel>
          {locationId && assignableUsers.length > 0 && (
            <Box sx={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              mb: "6px",
              fontFamily: FONT_MONO,
              fontSize: "11px",
              fontWeight: 500,
              color: SECTION_LABEL_COLOR,
            }}>
              <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: "#3B82F6", flexShrink: 0 }} />
              {translate("teams.filteredByLocation") || "Filtered by"} <strong>{selectedLocation?.name || ""}</strong>
            </Box>
          )}
          {locationId && assignableUsers.length > 0 ? (
            /* Location-filtered mode: show assignable users from teams at this location */
            <Autocomplete
              size="small"
              options={assignableUsers}
              value={assignableUsers.find(u => u.userId === assignedToUserId) || null}
              loading={assignableUsersLoading}
              getOptionLabel={(u) => u.userName}
              isOptionEqualToValue={(opt, val) => opt.userId === val.userId}
              onChange={(_, val) => {
                if (val) {
                  setAssignedToUserId(val.userId);
                  setAssignedToUserName(val.userName);
                  // Also resolve the User object for the selectedUser state
                  const fullUser = users.find(u => u.id === val.userId);
                  setSelectedUser(fullUser || null);
                } else {
                  setAssignedToUserId("");
                  setAssignedToUserName("");
                  setSelectedUser(null);
                }
              }}
              slotProps={autocompletePaperSlotProps}
              renderOption={(props, option) => {
                const { key, ...rest } = props as any;
                const parts = option.userName.split(" ");
                const initials = parts.length >= 2
                  ? `${parts[0][0] || ""}${parts[1][0] || ""}`
                  : (parts[0]?.[0] || "");
                const colorIdx = assignableUsers.indexOf(option);
                return (
                  <li key={key} {...rest} style={{ padding: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "10px", width: "100%", padding: "9px 12px", borderRadius: "8px" }}>
                      <Box sx={{
                        width: 28, height: 28, borderRadius: "50%",
                        backgroundColor: getAvatarColor(colorIdx),
                        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                      }}>
                        <Box sx={{ fontFamily: FONT_SORA, fontSize: "10px", fontWeight: 600, color: "#FFFFFF" }}>{initials}</Box>
                      </Box>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: "1px", minWidth: 0, flex: 1 }}>
                        <Box className="option-primary-text" sx={{ fontFamily: FONT_SORA, fontSize: "13px", fontWeight: 600, color: TEXT_PRIMARY }}>
                          {option.userName}
                        </Box>
                        <Box sx={{ fontFamily: FONT_MONO, fontSize: "10px", fontWeight: 400, color: SECTION_LABEL_COLOR }}>
                          {option.teamName || "—"} · {option.openWorkOrderCount} open
                        </Box>
                      </Box>
                    </Box>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={translate("fields.selectUser")}
                  sx={panelTextFieldSx}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {assignableUsersLoading ? <CircularProgress size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          ) : (
            /* No location filter: show all company users (original behavior) */
            <Autocomplete
              size="small"
              options={users}
              value={selectedUser}
              loading={usersLoading}
              getOptionLabel={(u) => `${u.firstName} ${u.lastName}`}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              onChange={(_, val) => {
                setSelectedUser(val);
                setAssignedToUserId(val?.id || "");
                setAssignedToUserName(val ? `${val.firstName} ${val.lastName}` : "");
              }}
              slotProps={autocompletePaperSlotProps}
              renderOption={(props, option) => {
                const { key, ...rest } = props as any;
                const initials = `${option.firstName?.[0] || ""}${option.lastName?.[0] || ""}`;
                const colorIdx = users.indexOf(option);
                return (
                  <li key={key} {...rest} style={{ padding: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "10px", width: "100%", padding: "9px 12px", borderRadius: "8px" }}>
                      <Box sx={{
                        width: 28, height: 28, borderRadius: "50%",
                        backgroundColor: getAvatarColor(colorIdx),
                        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                      }}>
                        <Box sx={{ fontFamily: FONT_SORA, fontSize: "10px", fontWeight: 600, color: "#FFFFFF" }}>{initials}</Box>
                      </Box>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: "1px", minWidth: 0 }}>
                        <Box className="option-primary-text" sx={{ fontFamily: FONT_SORA, fontSize: "13px", fontWeight: 600, color: TEXT_PRIMARY }}>
                          {option.firstName} {option.lastName}
                        </Box>
                        <Box sx={{ fontFamily: FONT_MONO, fontSize: "10px", fontWeight: 400, color: SECTION_LABEL_COLOR }}>
                          {option.department || "—"}
                        </Box>
                      </Box>
                    </Box>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={translate("fields.selectUser")}
                  sx={panelTextFieldSx}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {usersLoading ? <CircularProgress size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          )}
        </Box>

        {/* Team */}
        <Box sx={{
          mb: "12px",
          "& .MuiOutlinedInput-root": {
            backgroundColor: INPUT_BG,
            borderRadius: "8px",
            "& .MuiOutlinedInput-notchedOutline": { borderColor: INPUT_BORDER, borderWidth: "1.5px" },
            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: FOCUS_COLOR },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: FOCUS_COLOR, borderWidth: "1.5px" },
          },
          "& .MuiSvgIcon-root": { color: "#4A4F57" },
          /* Unified shell for Team dropdown popup */
          "& .MuiPaper-root": {
            backgroundColor: "#FFFFFF",
            borderRadius: "12px",
            border: `1px solid ${BORDER_COLOR}`,
            boxShadow: "0 8px 32px rgba(12,17,23,0.08)",
            padding: "6px",
            mt: "4px",
          },
          "& .MuiMenuItem-root": {
            fontFamily: FONT_SORA,
            fontSize: "13px",
            fontWeight: 600,
            color: TEXT_PRIMARY,
            borderRadius: "8px",
            margin: "1px 0",
            padding: "9px 12px",
            "&:hover": { backgroundColor: "#F7F5F0" },
            "&.Mui-selected": {
              backgroundColor: "rgba(212,87,43,0.06)",
              color: FOCUS_COLOR,
              "&:hover": { backgroundColor: "rgba(212,87,43,0.10)" },
            },
          },
        }}>
          <TeamPicker
            value={teamId}
            onChange={(v) => {
              setTeamId(v);
              // Do NOT clear manually-assigned person when a team is selected.
              // Auto-assign is a server-side fallback — only used when no person is set.
            }}
            autoAssign={autoAssignToLeastLoaded}
            onAutoAssignChange={(v) => {
              setAutoAssignToLeastLoaded(v);
              // Do NOT clear manually-assigned person when auto-assign toggles.
            }}
            locationId={locationId || undefined}
            locationName={selectedLocation?.name}
          />
        </Box>

        <SectionDivider />

        {/* ─── SCHEDULE Section ────────────────────────────── */}
        <SectionLabel>{translate("createPanel.schedule") || "Schedule"}</SectionLabel>

        <Box sx={{
          display: "flex",
          flexDirection: isPhone ? "column" : "row",
          gap: 2,
          mb: "12px",
          "& .MuiSvgIcon-root": { color: "#4A4F57" },
          "& .MuiOutlinedInput-root": {
            "&:hover fieldset": { borderColor: `${FOCUS_COLOR} !important` },
            "&.Mui-focused fieldset": { borderColor: `${FOCUS_COLOR} !important` },
          },
          "& .MuiIconButton-root": {
            color: `${FOCUS_COLOR} !important`,
            "&:hover": { backgroundColor: "rgba(212,87,43,0.06) !important" },
          },
        }}>
          <Box sx={{ flex: 1 }}>
            <FormFieldLabel>{translate("fields.startDate")}</FormFieldLabel>
            <FormDatePicker
              value={parseDate(scheduledStartDate)}
              onChange={(d) => {
                setScheduledStartDate(formatDate(d));
                if (!d) {
                  setScheduledStartTime("");
                  setShowTimePicker(false);
                }
              }}
              popperSx={datePickerTerracottaPopperSx}
            />
            <ScheduleTimePicker
              dateValue={scheduledStartDate}
              timeValue={scheduledStartTime}
              onTimeChange={setScheduledStartTime}
              showPicker={showTimePicker}
              onTogglePicker={setShowTimePicker}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <FormFieldLabel>{translate("fields.dueDate")}</FormFieldLabel>
            <FormDatePicker
              value={parseDate(dueDate)}
              onChange={(d) => setDueDate(formatDate(d))}
              popperSx={datePickerTerracottaPopperSx}
            />
          </Box>
        </Box>

        {/* ─── Edit-mode extra fields (end date, duration, notes) ── */}
        {isEditMode && (
          <>
            <Box sx={{
              display: "flex",
              flexDirection: isPhone ? "column" : "row",
              gap: 2,
              mb: "12px",
              "& .MuiIconButton-root": {
                color: `${FOCUS_COLOR} !important`,
                "&:hover": { backgroundColor: "rgba(212,87,43,0.06) !important" },
              },
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": { borderColor: `${FOCUS_COLOR} !important` },
                "&.Mui-focused fieldset": { borderColor: `${FOCUS_COLOR} !important` },
              },
            }}>
              <Box sx={{ flex: 1 }}>
                <FormFieldLabel>{translate("fields.scheduledEnd") || "End Date"}</FormFieldLabel>
                <FormDatePicker
                  value={parseDate(scheduledEndDate)}
                  onChange={(d) => setScheduledEndDate(formatDate(d))}
                  popperSx={datePickerTerracottaPopperSx}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <FormFieldLabel>{translate("fields.estimatedDuration") || "Est. Duration"} (min)</FormFieldLabel>
                <FormTextField
                  value={estimatedDuration}
                  onChange={setEstimatedDuration}
                  placeholder="e.g. 60"
                />
              </Box>
            </Box>

            <Box sx={{ mb: "12px" }}>
              <FormFieldLabel>{translate("fields.completionNotes") || "Completion Notes"}</FormFieldLabel>
              <FormTextarea
                value={completionNotes}
                onChange={setCompletionNotes}
                rows={2}
                minHeight="56px"
                placeholder={translate("panel.completionNotesPlaceholder") || "Add completion notes..."}
              />
            </Box>
          </>
        )}

        {/* ─── Recurrence (Admin only, all WO types) ────────── */}
        {showRecurrenceOption && (
          <Box sx={{
            mt: "4px",
            mb: "12px",
            transition: "all 0.2s ease",
          }}>
            {/* Toggle row: warm tint pill — only THIS row gets the tinted bg */}
            <Box
              onClick={() => setIsRecurring(!isRecurring)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "pointer",
                userSelect: "none",
                borderRadius: "12px",
                backgroundColor: isRecurring ? "rgba(212, 87, 43, 0.06)" : "#F7F5F0",
                border: isRecurring ? "1px solid rgba(212, 87, 43, 0.20)" : `1px solid ${BORDER_COLOR}`,
                padding: "10px 16px",
                transition: "all 0.2s ease",
              }}
            >
              <Box sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontFamily: FONT_SORA,
                fontSize: "13px",
                fontWeight: 600,
                color: isRecurring ? FOCUS_COLOR : TEXT_PRIMARY,
              }}>
                <EventRepeatOutlined sx={{ fontSize: 18, color: isRecurring ? FOCUS_COLOR : SECTION_LABEL_COLOR }} />
                {translate("recurrence.makeRecurring")}
              </Box>
              <Switch
                checked={isRecurring}
                onChange={(_, checked) => setIsRecurring(checked)}
                size="small"
                onClick={(e) => e.stopPropagation()}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: FOCUS_COLOR },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: FOCUS_COLOR },
                }}
              />
            </Box>
            {/* Expanded recurrence content — clean white background */}
            {isRecurring && (
              <Box sx={{ mt: "14px" }}>
                <RecurrenceRulePicker
                  value={recurrenceRule}
                  onChange={setRecurrenceRule}
                  startDate={dueDate || new Date().toISOString().substring(0, 10)}
                  previewDates={recurrencePreview}
                />
              </Box>
            )}
          </Box>
        )}

        <SectionDivider />

        {/* ─── ATTACHMENTS Section ─────────────────────────── */}
        <SectionLabel>{translate("createPanel.attachments") || "Attachments"}</SectionLabel>

        <Box sx={{
          mb: "12px",
          /* Override FormFileUpload: hide cloud icon + formats line, restyle border */
          "& > div > div:first-of-type": {
            border: `1.5px dashed ${BORDER_COLOR}`,
            backgroundColor: "transparent",
            height: "56px !important",
            "&:hover": { borderColor: FOCUS_COLOR, backgroundColor: "transparent" },
          },
          "& > div > div:first-of-type > .MuiSvgIcon-root": { display: "none" },
          "& > div > div:first-of-type > div": { display: "none" },
          "& > div > div:first-of-type > .MuiTypography-root": {
            fontSize: "12px",
            fontWeight: 500,
            color: SECTION_LABEL_COLOR,
          },
        }}>
          <FormFileUpload
            files={files}
            onFilesChange={setFiles}
            dropZoneHeight="56px"
            labels={{
              dragAndDrop: "+ Attach files (photos, docs, error logs)",
              supportedFormats: "",
            }}
          />
        </Box>
      </Box>

      {/* ─── Sticky Footer ────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 2,
          padding: isPhone ? "16px" : "16px 24px",
          paddingRight: isPhone ? "16px" : "72px",
          borderTop: `1px solid ${BORDER_COLOR}`,
          flexShrink: 0,
          backgroundColor: PANEL_BG,
        }}
      >
        <Box
          onClick={onClose}
          sx={{
            fontFamily: FONT_SORA,
            fontSize: "13px",
            fontWeight: 500,
            color: TEXT_BODY,
            cursor: "pointer",
            padding: "8px 16px",
            borderRadius: "8px",
            transition: "all 0.15s ease",
            "&:hover": { backgroundColor: "#F2F0EB" },
          }}
        >
          {translate("common:buttons.cancel") || "Cancel"}
        </Box>
        <Box
          onClick={isValid && !submitting ? handleSubmit : undefined}
          sx={{
            fontFamily: FONT_SORA,
            fontSize: "13px",
            fontWeight: 600,
            color: "#FFFFFF",
            backgroundColor: isValid && !submitting ? FOCUS_COLOR : "#C1BDB7",
            padding: "10px 28px",
            borderRadius: "8px",
            cursor: isValid && !submitting ? "pointer" : "default",
            display: "flex",
            alignItems: "center",
            gap: 1,
            transition: "all 0.15s ease",
            "&:hover": isValid && !submitting
              ? { backgroundColor: "#BF4D26", transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(212,87,43,0.25)" }
              : {},
          }}
        >
          {submitting && <CircularProgress size={14} sx={{ color: "#FFFFFF" }} />}
          {isEditMode ? (translate("panel.saveChanges") || "Save Changes") : translate("createWorkOrder")}
        </Box>
      </Box>
    </Box>
  );
};

export default CreateWorkOrderPanel;
